import { useState } from 'react';
import { IoCubeOutline } from "react-icons/io5";
import { FaBalanceScaleRight, FaCalculator, FaExclamationTriangle } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";


export default function PartInfoCard({ partInfo, materialSpecs }) {
  const [showCalcDetails, setShowCalcDetails] = useState(false);

  // Get weight calculation details if available
  const weightCalc = partInfo?.weightCalculation;
  return (
    <div className="space-y-6">
      {/* Part Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100
           overflow-hidden p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IoCubeOutline className="w-5 h-5 text-green-900" />
          Part Information
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Part Number</div>
            <div className="font-medium text-gray-900">
              {partInfo.partNumber || 'N/A'}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500">Revision</div>
            <div className="font-medium text-gray-900">
              {partInfo.revision || 'N/A'}
            </div>
          </div>

          <div className="col-span-2">
            <div className="text-sm text-gray-500">Description</div>
            <div className="font-medium text-gray-900">
              {partInfo.description || 'N/A'}
            </div>
          </div>

          {/* Weight with status badge and calculation details */}
          <div className="col-span-2 bg-gray-50 rounded-lg overflow-hidden">
            <div className="flex items-center gap-4 p-3">
              <FaBalanceScaleRight className="w-8 h-8 text-green-900" />
              <div className="flex-1">
                <div className="text-sm text-gray-500">Weight</div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-gray-900">
                    {partInfo.weightKg != null
                      ? `${partInfo.weightKg.toFixed(3)} kg`
                      : 'Not available'}
                  </span>
                  {partInfo.weightStatus && (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        partInfo.weightStatus === 'Calculated'
                          ? 'bg-purple-100 text-purple-800'
                          : partInfo.weightStatus === 'Estimated'
                            ? 'bg-blue-100 text-blue-800'
                          : partInfo.weightStatus === 'Explicitly Stated'
                            ? 'bg-green-100 text-green-800'
                            : partInfo.weightStatus === 'Calculation Failed'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {partInfo.weightStatus}
                    </span>
                  )}
                </div>
              </div>

              {/* Toggle button for calculation details */}
              {(weightCalc?.calculationDetails || weightCalc?.error) && (
                <button
                  onClick={() => setShowCalcDetails(!showCalcDetails)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <FaCalculator className="w-4 h-4" />
                  {showCalcDetails ? 'Hide' : 'Show'} Formula
                  {showCalcDetails ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                </button>
              )}
            </div>

            {/* Calculation Details Panel */}
            {showCalcDetails && weightCalc && (
              <div className="border-t border-gray-200 p-4 bg-white">
                {weightCalc.error ? (
                  /* Error State - calculation couldn't be completed */
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FaExclamationTriangle className="w-4 h-4 text-amber-600" />
                      Weight Calculation Not Possible
                    </h4>

                    {/* Error Summary */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800 font-medium">
                        {weightCalc.error}
                      </p>
                    </div>

                    {/* Missing Information */}
                    {weightCalc.missingInformation && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                        <div className="text-xs text-red-600 font-medium mb-2">Missing Information</div>
                        <p className="text-sm text-red-800 whitespace-pre-wrap">
                          {weightCalc.missingInformation}
                        </p>
                      </div>
                    )}

                    {/* Available Information */}
                    {weightCalc.availableInformation && (
                      <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                        <div className="text-xs text-green-600 font-medium mb-2">Available Information Found</div>
                        <p className="text-sm text-green-800 whitespace-pre-wrap">
                          {weightCalc.availableInformation}
                        </p>
                      </div>
                    )}

                    {/* Materials Found - when multiple materials cause ambiguity */}
                    {weightCalc.materialsFound && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="text-xs text-amber-700 font-medium mb-2">Materials Found on Drawing</div>
                        <p className="text-sm text-amber-800 whitespace-pre-wrap">
                          {weightCalc.materialsFound}
                        </p>
                        <p className="text-xs text-amber-600 mt-2 italic">
                          Could not determine which material is the primary (main body) material.
                        </p>
                      </div>
                    )}

                    {/* Detailed Analysis Steps */}
                    {weightCalc.calculationDetails && (
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-2">Analysis Details</div>
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                          {weightCalc.calculationDetails}
                        </pre>
                      </div>
                    )}

                    {/* Component Description if available */}
                    {weightCalc.componentDescription && (
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                        <div className="text-xs text-blue-600 font-medium mb-2">Component Description</div>
                        <p className="text-sm text-blue-800 whitespace-pre-wrap">
                          {weightCalc.componentDescription}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Success State - show calculation formula */
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FaCalculator className="w-4 h-4 text-purple-600" />
                      Weight Calculation Formula
                    </h4>

                    {/* Primary Material - highlighted */}
                    {weightCalc.material && (
                      <div className="p-3 bg-purple-100 border border-purple-200 rounded-lg">
                        <div className="text-xs text-purple-700 font-medium mb-1">Primary Material (used for calculation)</div>
                        <div className="text-base font-semibold text-gray-900">{weightCalc.material}</div>
                      </div>
                    )}

                    {/* Secondary Materials - if any */}
                    {weightCalc.secondaryMaterials && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="text-xs text-gray-600 font-medium mb-1">Secondary Materials (not included in weight)</div>
                        <div className="text-sm text-gray-700">{weightCalc.secondaryMaterials}</div>
                      </div>
                    )}

                    {/* Calculation Parameters */}
                    {(weightCalc.density_g_cm3 || weightCalc.netVolume_cm3) && (
                      <div className="grid grid-cols-2 gap-3 p-3 bg-purple-50 rounded-lg">
                        {weightCalc.density_g_cm3 && (
                          <div>
                            <div className="text-xs text-purple-600 font-medium">Density</div>
                            <div className="text-sm font-semibold text-gray-900">{weightCalc.density_g_cm3} g/cm³</div>
                          </div>
                        )}
                        {weightCalc.netVolume_cm3 && (
                          <div>
                            <div className="text-xs text-purple-600 font-medium">Net Volume</div>
                            <div className="text-sm font-semibold text-gray-900">{weightCalc.netVolume_cm3.toFixed(2)} cm³</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step-by-step calculation */}
                    {weightCalc.calculationDetails && (
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-2">
                          {partInfo.weightStatus === 'Estimated' ? 'Estimation Logic & Range' : 'Calculation Steps'}
                        </div>
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                          {weightCalc.calculationDetails}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Part Summary */}
        {partInfo.partSummary && (
          <div className="mt-4 p-4 bg-amber-50 rounded-lg">
            <div className="text-sm font-medium text-gray-700 mb-1">Summary</div>
            <p className="text-sm text-gray-600">{partInfo.partSummary}</p>
          </div>
        )}
      </div>

      {/* Material Specifications */}
      {materialSpecs && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100
           overflow-hidden p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Material Specifications
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="text-sm text-gray-500">Material</div>
              <div className="font-medium text-gray-900 text-lg">
                {materialSpecs.material || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500">Standard</div>
              <div className="font-medium text-gray-900">
                {materialSpecs.standard || 'N/A'}
              </div>
            </div>

            {materialSpecs.thickness_mm && (
              <div>
                <div className="text-sm text-gray-500">Thickness</div>
                <div className="font-medium text-gray-900">
                  {materialSpecs.thickness_mm} mm
                </div>
              </div>
            )}

            {materialSpecs.treatments && materialSpecs.treatments.length > 0 && (
              <div className="col-span-2">
                <div className="text-sm text-gray-500 mb-1">Treatments</div>
                <div className="flex flex-wrap gap-2">
                  {materialSpecs.treatments.map((treatment, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded"
                    >
                      {treatment}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {materialSpecs.finishRequirements && (
              <div className="col-span-2">
                <div className="text-sm text-gray-500">Finish Requirements</div>
                <div className="font-medium text-gray-900">
                  {materialSpecs.finishRequirements}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
